import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Sparkles } from "lucide-react";
import { formatMoney } from "../lib/currencyDisplay";
import { RATE_VARIANCE_HINT } from "../lib/tripPricingDisplay";

/**
 * Renter-facing pricing tip. Vehicles: demand-only copy (never host list price).
 * Parking: hourly rate for the spot.
 */
export default function PriceInsight({ kind, itemId, unit = "/day" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const isVehicle = kind === "vehicle";

  useEffect(() => {
    if (!itemId || isVehicle) return;
    const path = `${API}/parking/${itemId}/price-insight`;
    axios.get(path).then((r) => setData(r.data)).catch(() => setError(true));
  }, [kind, itemId, isVehicle]);

  if (isVehicle) {
    return (
      <div
        className="zc-card p-5"
        style={{ background: "linear-gradient(180deg, var(--zc-bg-2) 0%, var(--zc-secondary-soft) 100%)" }}
        data-testid="price-insight-vehicle"
      >
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[var(--zc-accent)]" />
          <div className="eyebrow">Trip pricing</div>
        </div>
        <p
          className="zc-hint text-[var(--zc-text-2)] mt-3 leading-relaxed"
          data-testid="price-insight-rationale"
          title={RATE_VARIANCE_HINT}
        >
          {RATE_VARIANCE_HINT} Select your dates to see the rate for your trip.
        </p>
      </div>
    );
  }

  if (error || !data) return null;

  const { host_price, currency = "USD" } = data;
  const money = (amount) => formatMoney(amount, currency);

  return (
    <div
      className="zc-card p-5"
      style={{ background: "linear-gradient(180deg, var(--zc-bg-2) 0%, var(--zc-secondary-soft) 100%)" }}
      data-testid={`price-insight-${kind}`}
    >
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-[var(--zc-accent)]" />
        <div className="eyebrow">Trip pricing</div>
      </div>

      <div className="mt-4">
        <div className="eyebrow">From</div>
        <div className="zc-price text-2xl mt-0.5">
          {money(host_price)}
          <span className="zc-unit normal-case tracking-normal">{unit}</span>
        </div>
      </div>

      <p
        className="zc-hint text-[var(--zc-text-2)] mt-3 leading-relaxed"
        data-testid="price-insight-rationale"
      >
        Hourly rate for this spot. Your total is based on the hours you select at checkout.
      </p>
    </div>
  );
}
